<script setup lang="ts">
import { computed } from "vue";
import { trackReportFeature } from "@/analytics/report-analytics";
import { trackDebounced } from "@/analytics/track-debounce";
import { useTranslation } from "i18next-vue";
import ReportContent from "../layout/ReportContent.vue";
import TextContentEditor from "./text-content/TextContentEditor.vue";
import { useUpdateReportSilent } from "@/stores/queries/reports.query";
import type { Diagnostic, Report } from "@/types";

const props = defineProps<{
  report: Report;
  diagnostic: Diagnostic;
}>();

const { t } = useTranslation();

const reportIdRef = computed(() => props.report.id);
const { mutateAsync: updateReport } = useUpdateReportSilent(reportIdRef);

const summarySuggestion = computed<string | null>(
  () => props.diagnostic.detectedConclusions ?? null,
);

const recommendationsSuggestion = computed<string | null>(
  () => props.diagnostic.detectedPreconisation ?? null,
);

const handleSaveSummary = async (value: string) => {
  trackDebounced("report_conclusions", () =>
    trackReportFeature("conclusions_edited"),
  );
  await updateReport({ conclusion: value });
};

const handleSaveRecommendations = async (value: string) => {
  trackDebounced("report_conclusions", () =>
    trackReportFeature("conclusions_edited"),
  );
  await updateReport({ preconisation: value });
};
</script>

<template>
  <ReportContent>
    <div class="conclusions-page">
      <TextContentEditor
        :model-value="report.conclusion"
        :title="t('report:summary.editorLabel')"
        icon="bi-clipboard-check"
        :previous-report-content="summarySuggestion"
        :previous-report-label="t('report:textContent.showPreviousReport')"
        :editor-placeholder="t('report:summary.editorPlaceholder')"
        :hint="t('report:summary.hint')"
        @save="handleSaveSummary"
      />

      <TextContentEditor
        :model-value="report.preconisation"
        :title="t('report:recommendations.editorLabel')"
        icon="bi-lightbulb"
        :previous-report-content="recommendationsSuggestion"
        :previous-report-label="t('report:textContent.showPreviousReport')"
        :editor-placeholder="t('report:recommendations.editorPlaceholder')"
        :hint="t('report:recommendations.hint')"
        @save="handleSaveRecommendations"
      />
    </div>
  </ReportContent>
</template>

<style scoped lang="scss">
.conclusions-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 1100px;
}
</style>
